// src/components/exam-player/renderers/ClozeRegexRenderer.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// استایل رشته‌ای → آبجکت
function parseStyleString(str = "") {
  const out = {};
  str.split(";").forEach((decl) => {
    const [rawProp, ...rest] = decl.split(":");
    if (!rawProp || !rest.length) return;
    const prop = rawProp.trim();
    const value = rest.join(":").trim();
    if (!prop) return;
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    out[camel] = value;
  });
  return out;
}

// HTML + {{1}} + regexها → React nodes
function renderHtmlWithRegexBlanks(html, answers, setAnswers, questionNumber, regexes, t) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const rxBlank = /\{\{(\w+)\}\}/g;

  const toReact = (node, key) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const parts = [];
      let lastIndex = 0;
      let m;
      while ((m = rxBlank.exec(node.textContent))) {
        const before = node.textContent.slice(lastIndex, m.index);
        if (before) parts.push(before);

        const blankKey = m[1]; // مثلاً "1"
        const idx = Number(blankKey) - 1; // برای دسترسی به regex مربوطه
        const regexDef = regexes[idx];
        const label =
          regexDef?.placeholder?.fa ||
          regexDef?.placeholder?.en ||
          t("cloze_regex.blank_key", {
            defaultValue: `Blank ${blankKey}`,
            key: blankKey,
          });

        const value = answers[blankKey] ?? "";
        const placeholderText = label;

        parts.push(
          <input
            key={`${key}-blank-${m.index}`}
            data-blank={blankKey}
            value={value}
            onChange={(e) =>
              setAnswers((prev) => ({ ...prev, [blankKey]: e.target.value }))
            }
            onBlur={(e) => {
              const v = e.target.value;
              const trimmed = v.replace(/\s+/g, " ").trim();
              if (trimmed !== v)
                setAnswers((prev) => ({ ...prev, [blankKey]: trimmed }));
            }}
            className="mx-1 inline-block align-baseline min-w-[120px] border-b border-gray-400 px-1 py-0.5 focus:outline-none focus:border-indigo-500"
            placeholder={placeholderText}
            aria-label={`blank-${blankKey}`}
          />
        );

        lastIndex = rxBlank.lastIndex;
      }
      const tail = node.textContent.slice(lastIndex);
      if (tail) parts.push(tail);
      return parts;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      const props = { key };

      for (const attr of node.attributes) {
        const name = attr.name;
        const value = attr.value;

        if (name === "class") props.className = value;
        else if (name === "style") props.style = parseStyleString(value);
        else if (name.startsWith("data-")) props[name] = value;
        else {
          const map = {
            colspan: "colSpan",
            rowspan: "rowSpan",
            cellpadding: "cellPadding",
            cellspacing: "cellSpacing",
            for: "htmlFor",
          };
          const reactName = map[name] || name;
          props[reactName] = value;
        }
      }

      const children = [];
      let i = 0;
      for (let child = node.firstChild; child; child = child.nextSibling) {
        children.push(toReact(child, `${key}-${i++}`));
      }

      return React.createElement(tag, props, ...children);
    }

    return null;
  };

  const children = [];
  let i = 0;
  for (let child = doc.body.firstChild; child; child = child.nextSibling) {
    children.push(toReact(child, `root-${i++}`));
  }
  return children;
}

export default function ClozeRegexRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem,
}) {
  const { t } = useTranslation();

  const textHtml = item?.body?.text?.fa || item?.body?.text?.en || "";
  const regexes = Array.isArray(item?.body?.clozeRegexes)
    ? item.body.clozeRegexes
    : [];

  const rx = /\{\{(\w+)\}\}/g;
  const keysInText = useMemo(() => {
    const keys = [];
    let m;
    while ((m = rx.exec(textHtml))) keys.push(m[1]); // "1", "2", ...
    return keys;
  }, [textHtml]);

  const [answers, setAnswers] = useState(() => {
    const base = {};
    keysInText.forEach((k) => {
      base[k] =
        initialAnswers && typeof initialAnswers[k] === "string"
          ? initialAnswers[k]
          : "";
    });
    return base;
  });

  useEffect(() => {
    const base = {};
    keysInText.forEach((k) => {
      base[k] =
        initialAnswers && typeof initialAnswers[k] === "string"
          ? initialAnswers[k]
          : "";
    });
    setAnswers(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textHtml]);

  useEffect(() => {
    onAnswerChange?.(answers);
  }, [answers, onAnswerChange]);

  if (!textHtml) {
    return (
      <div className="text-sm text-red-500">
        {t("preview.cloze_no_text_error")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        className="
          prose prose-sm max-w-none leading-7
          [&_table]:table-fixed [&_table]:w-full
          [&_th]:px-3 [&_th]:py-2 [&_td]:px-3 [&_td]:py-2
          [&_thead_th]:text-left
        "
      >
        {renderHtmlWithRegexBlanks(
          textHtml,
          answers,
          setAnswers,
          questionNumber,
          regexes,
          t
        )}
      </div>

      {!hideStem && (
        <div className="mt-2 text-xs text-gray-600">
          {t("preview.blanks_count", { n: keysInText.length })}
        </div>
      )}
    </div>
  );
}
